import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, toNum } from "@shared/utils/CommonUtils";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, globalMenu } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTree from "@shared/components/GridV2/SnTree";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { ColDef } from "ag-grid-community";
import { v4 as uuid } from "uuid";
import SN_PMS_PJT_REG_004_POP_001 from "./SN_PMS_PJT_REG_004_POP_001";
import SN_PMS_PJT_REG_004_POP_002 from "./SN_PMS_PJT_REG_004_POP_002";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnTab from "@shared/components/SnTab";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
interface Props {
  Menuprops: menuInfo;
}

const GRID_EXTRA_HEADER_MENUS = [
  { id: "extra_item_setting", name: "부가항목설정", visible: true },
];

const SN_PMS_PJT_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef(),
    grid: React.createRef(),
  });
  const nodeFilterRef = useRef<string[]>([]);

  const [formData, setFormData] = useState<any>({});
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    tree: [],
    grid: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    tree: [],
    grid: [],
    sheet1: [],
  });

  const sheetref1 = useRef<any>(null); // excel download 용
  const [isTreeExpanded, setIsTreeExpanded] = useState<boolean>(true);
  const addParentRef = useRef<any>(null);
  const focusedTreeRowRef = useRef<any>(null);
  const isBatchAddingRef = useRef<boolean>(false);
  const [selectedDocu, setSelectedDocu] = useState<string>(""); // 파일 업로드용 키 추가
  const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);

  // 조회 데이터의 js_addinfo 값을 동적 컬럼(cd_flag)으로 펼쳐서 그리드 표시
  const hydrateAddInfoColumns = useCallback(
    (rows: any[]) => hydrateRowsWithAddInfo(rows, addInfoFlags),
    [addInfoFlags]
  );

  // 저장 직전에 동적 컬럼 값을 js_addinfo로 다시 묶어 payload 생성
  const buildJsAddinfoFromRow = useCallback(
    (row: any) => buildJsAddinfoFromFlags(row, addInfoFlags),
    [addInfoFlags]
  );

  // 메뉴별 sys_addinfo(cd_flag) 목록 조회
  const loadAddInfoFlags = useCallback(async (idTarget = "grid2") => {
    const result: any = await fnGetDataSet({
      id: "sys_addinfo_pop_01_01",
      params: {
        cd_firm: userInfo?.cd_firm ?? "",
        cd_menu: Menuprops?.cd_menu ?? "",
        id_target: idTarget,
      },
    });
    const rows = result?.[0] || [];
    const flags = extractAddInfoFlags(rows);
    setAddInfoFlags(flags);
  }, [Menuprops?.cd_menu, userInfo?.cd_firm]);
  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          tree: rtn.grid.grid1,
          grid: rtn.grid.grid2,
          sheet1: rtn.grid.sheet1,
        });
        // 메뉴 오픈 시 동적 부가항목 키 목록도 함께 로드
        await loadAddInfoFlags("grid2");
      } catch (error) {}
    };
    fetchform();
  }, [loadAddInfoFlags]);

  useEffect(() => {
    // PMS 메뉴에서만 품목 도움창 전용 조회 ID를 사용하도록 플래그 주입
    searchRef.current?.applyPopupConditions?.({
      use_pms_item_popup: "Y",
    });
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
  }));

  const onSearchPanelValueChanged = useCallback((event: ValueChangedEvent) => {
    if (event.colId !== "cd_pjt") return {};

    const oldPjt = String(event.oldValue ?? "").trim();
    const newPjt = String(event.newValue ?? "").trim();
    if (oldPjt === newPjt) return {};

    focusedTreeRowRef.current = null;
    nodeFilterRef.current = [];
    setSelectedDocu("");
    setDataSources({
      tree: [],
      grid: [],
      sheet1: [],
    });

    return {
      cd_item: "",
      nm_item: "",
      dc_item_spec: "",
      dc_item_unit: "",
      nm_model: "",
      yn_use: "",
      no_rev: "",
    };
  }, []);

  // 조회조건으로 트리/그리드 데이터를 조회하고 화면 데이터를 갱신
  const onSearch = async () => {
    try {
      const form = searchRef.current?.getFormdata?.() ?? {};

      if (!form?.cd_pjt?.trim()) {
        // showDialog("alter", "프로젝트 코드를 선택하시기 바랍니다");
        return false;
      }

      if (!form?.cd_item?.trim()) {
        // showDialog("alter", "품목을 선택하시기 바랍니다");
        return false;
      }

      const modifiedRows = gridRefs.current["grid"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirmCancel",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const isSaved: any = await onSave();
          if (!isSaved) {
            return;
          }
        } else if (confirm === undefined) {
          return;
        }
      }

      const params = {
        id: "sn_pms_pjt_reg_004_01",
        params: form,
      };

      focusedTreeRowRef.current = null;
      gridRefs.current["tree"].current.resetUpdate();
      gridRefs.current["grid"].current.resetUpdate();
      // 조회 전 이전 선택/포커스/필터 초기화
      gridRefs.current["tree"].current.clearFocus?.();
      gridRefs.current["grid"].current.clearFocus?.();
      gridRefs.current["grid"].current.resetFilters?.();
      nodeFilterRef.current = [];
      const data: any = await fnGetDataSet(params);
      if (data) {
        // 조회 결과의 js_addinfo를 동적 컬럼(cd_flag) 필드로 펼친 후 바인딩
        const gridRows = hydrateAddInfoColumns(data[0] || []);
        const treeData = convertToTreeData(gridRows);
        setDataSources({
          tree: treeData,
          grid: gridRows,
          sheet1: gridRows,
        });
        setSelectedDocu(form.cd_pjt);
      }
    } catch (error) {}
  };

  const onInsert = async () => {
    // 상단 공통 추가 버튼은 사용하지 않음.
    // 추가는 그리드 내부 addRow(하위추가) 버튼 경로로만 통일.
    return;
  };

  const onDelete = () => {
    // 상단 공통 삭제 버튼은 사용하지 않음.
    // 삭제는 그리드 내부 deleteRow 버튼 경로로만 통일.
    return;
  };

  // 그리드 변경분을 EBOMH/EBOML 저장 payload로 변환하여 저장
  const onSave = async () => {
    try {
      gridRefs.current["grid"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const gridGetChanges = gridRefs.current["grid"].current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const form = searchRef.current?.getFormdata?.() ?? {};
      const toLine = (row: any) => {
        const cdFirm = row.cd_firm ?? userInfo.cd_firm;
        const cdPjt = row.cd_pjt ?? form.cd_pjt;
        const cdUnit = row.cd_unit ?? form.cd_item;
        const cdItem = row.cd_item ?? form.cd_item;
        const noHst = row.no_hst ?? 0;
        const noRev = row.no_rev ?? "0";
        const stBom = row.st_bom ?? form.st_bom ?? "1";
        return {
          // UUID 컬럼에는 빈 문자열("")을 보내지 않고 null 처리
          no_id_ebomh: row.no_id_ebomh ? String(row.no_id_ebomh) : null,
          no_line: row.no_line ?? 0,
          no_line_p: row.no_line_p ?? 0,
          no_level: row.no_level ?? 0,
          no_seq: row.no_seq ?? 0,
          cd_matl: row.cd_matl ?? "",
          qt_just: row.qt_just ?? 0,
          rt_loss: row.rt_loss ?? 0,
          qt_need: row.qt_need ?? 0,
          dc_rmk: row.dc_rmk ?? "",
          fg_eco: row.fg_eco ?? null,
          cd_cb: row.cd_cb ?? "",
          no_cbs: row.no_cbs ?? "",
          cd_firm: cdFirm,
          cd_pjt: cdPjt,
          cd_unit: cdUnit,
          cd_item: cdItem,
          no_hst: toNum(noHst),
          no_rev: noRev,
          st_bom: stBom,
          dc_revision: row.dc_revision ?? form.dc_revision ?? "",
          cd_emp: row.cd_emp ?? form.cd_emp ?? userInfo.cd_emp ?? "",
          cd_gp: row.cd_gp ?? form.cd_gp ?? userInfo.cd_gp ?? "",
          cd_dept: row.cd_dept ?? form.cd_dept ?? userInfo.cd_dept ?? "",
          cd_user_reg: row.cd_user_reg ?? userInfo.cd_user ?? "",
          yn_use: row.yn_use ?? "Y",
          // 동적 부가항목 컬럼값을 jsonb 저장 포맷(js_addinfo)으로 구성
          js_addinfo: buildJsAddinfoFromRow(row),
        };
      };

      const insertRows = [...(gridGetChanges.insert || [])];
      const changedRowsAll = [
        ...insertRows,
        ...(gridGetChanges.update || []),
        ...(gridGetChanges.delete || []),
      ];
      const hasRootInChanges = changedRowsAll.some((row: any) => {
        const isLevelRoot = Number(row?.no_level ?? -1) === 0;
        const isLineRoot =
          Number(row?.no_line ?? -1) === 0 &&
          Number(row?.no_line_p ?? -1) === 0;
        return isLevelRoot || isLineRoot;
      });
      const existingRoot = (dataSources.grid || []).find(
        (row: any) =>
          Number(row?.no_level ?? -1) === 0 ||
          (Number(row?.no_line ?? -1) === 0 &&
            Number(row?.no_line_p ?? -1) === 0)
      );
      const hasExistingRootId = !!existingRoot?.no_id_ebomh;
      const needAutoRootInsert =
        insertRows.length > 0 && !hasRootInChanges && !hasExistingRootId;

      if (needAutoRootInsert) {
        insertRows.unshift({
          cd_firm: form.cd_firm ?? userInfo.cd_firm,
          cd_pjt: form.cd_pjt ?? "",
          cd_unit: form.cd_item ?? "",
          cd_item: form.cd_item ?? "",
          cd_matl: form.cd_item ?? "",
          no_hst: Number(form.no_hst ?? 1),
          no_rev: form.no_rev ?? "0",
          st_bom: form.st_bom ?? "1",
          no_line: 0,
          no_line_p: 0,
          no_level: 0,
          no_seq: 0,
          qt_just: 1,
          rt_loss: 0,
          qt_need: 1,
          dc_rmk: "",
          fg_eco: "I",
          cd_cb: "",
          no_cbs: "",
          dc_revision: form.dc_revision ?? "",
          cd_emp: form.cd_emp ?? userInfo.cd_emp ?? "",
          cd_gp: form.cd_gp ?? userInfo.cd_gp ?? "",
          cd_dept: form.cd_dept ?? userInfo.cd_dept ?? "",
          cd_user_reg: userInfo.cd_user ?? "",
          yn_use: "Y",
          no_id_ebomh: null,
        });
      }

      // update/delete 는 기존 데이터(UUID 보유)만 전송
      const updatableRows = (gridGetChanges.update || []).filter(
        (row: any) => !!row?.no_id_ebomh
      );
      const deletableRows = (gridGetChanges.delete || []).filter(
        (row: any) => !!row?.no_id_ebomh
      );

      const SaveData = {
        pms_pjt_eboml: {
          insert: insertRows.map(toLine),
          update: updatableRows.map(toLine),
          delete: deletableRows.map(toLine),
          modifiedRowcount:
            insertRows.length + updatableRows.length + deletableRows.length,
        },
      };

      const ok = await fnSave("SN_PMS_PJT_REG_004_SAVE_01", SaveData);
      if (!ok) {
        return;
      }

      gridRefs.current["grid"].current.resetUpdate();
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const onExcelDownLoad = async () => {
    try {
      const displayedCols = sheetref1.current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());

      sheetref1.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  // 엑셀 업로드 팝업 오픈 전 사전조건(필수 조회조건, 미저장 변경, 차수) 검증
  const onExcelUpload = async () => {
    try {
      const form = searchRef.current.getFormdata();
      if (!form.cd_pjt?.trim()) {
        await showDialog("alter", "프로젝트를 선택해 주세요.");
        return false;
      }
      if (!form.cd_item?.trim()) {
        await showDialog("alter", "모품목을 선택해 주세요.");
        return false;
      }
      // 저장되지 않은 변경이 있으면 업로드 차단
      const gridChanges = gridRefs.current["grid"].current?.getChange?.();
      if (gridChanges?.modifiedRowcount > 0) {
        await showDialog(
          "alter",
          "수정된 내용이 있습니다. 저장 후 엑셀 업로드를 진행해 주세요."
        );
        return false;
      }
      // 최신 차수(no_hst)가 1보다 크면 업로드 차단
      const maxHstParams = {
        id: "pms_pjt_ebom_max_hst",
        params: {
          cd_firm: userInfo?.cd_firm ?? "",
          cd_pjt: form.cd_pjt,
          cd_unit: form.cd_item,
        },
      };
      const maxHstData: any = await fnGetTableById(maxHstParams);
      const maxHst = Number(maxHstData?.[0]?.no_hst ?? 0);
      if (maxHst > 1) {
        await showDialog(
          "alter",
          "이미 변경 차수가 존재하여 엑셀 업로드를 사용할 수 없습니다."
        );
        return false;
      }
      const component = (
        <SN_PMS_PJT_REG_004_POP_001
          Menuprops={Menuprops} // 메뉴 정보 전달
          searchParams={{
            cd_firm: userInfo?.cd_firm ?? "",
            cd_pjt: form.cd_pjt,
            cd_unit: form.cd_item, // unit 파라미터명에 모품목 값 전달
          }}
          controllerName="SN_PMS_PJT_REG_004_SAVE_01" // 저장할 컨트롤러 이름
          userInfo={userInfo} // userInfo={userInfo}
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result) {
        await onSearch();
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "품목",
      field: "cd_matl_display",
      flex: 1, // 화면 가득 차게
      minWidth: 300,
      editable: false,
      cellRendererParams: {
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
        suppressHoverHighlight: false,
      },
    };
  }, []);

  const onFocusedRowChanged = (eventData: any) => {
    const gridApi = gridRefs.current["grid"].current;
    if (!gridApi) return;
    if (isBatchAddingRef.current) return;

    focusedTreeRowRef.current = eventData || null;
    const selectedPath = String(eventData?.path ?? "").trim();
    if (!selectedPath) {
      nodeFilterRef.current = [];
      gridApi.resetFilters();
      return;
    }

    const allCdMatls = (dataSources.grid || [])
      .filter((row: any) => {
        const p = String(row?.path ?? "");
        return p === selectedPath || p.startsWith(`${selectedPath}>`);
      })
      .map((row: any) => String(row?.node ?? ""))
      .filter((v: string) => !!v);

    if (allCdMatls.length > 0) {
      nodeFilterRef.current = allCdMatls;
      gridApi.applyFilters({ node: allCdMatls });
    } else {
      nodeFilterRef.current = [];
      gridApi.resetFilters();
    }
  };

  const getRowId = useCallback(
    (params: any) => String(params.data.node || params.data.cd_matl),
    []
  );

  const onGrid1BtnsClick = async (id: string) => {
    const api = gridRefs.current["tree"].current;
    if (!api) return;

    if (id === "btnToggleExpand") {
      api.setExpandedAll(!isTreeExpanded);
      setIsTreeExpanded(!isTreeExpanded);
    }
    if (id === "btnPhaseAdd") {
      const form = searchRef.current?.getFormdata?.() ?? {};
      const selectedNodes = api.getSelectedNodes?.() || [];
      if (!form?.cd_pjt?.trim()) {
        await showDialog("alter", "프로젝트를 선택해주세요.");
        return;
      }
      if (selectedNodes.length === 0) {
        await showDialog("alter", "차수추가할 반제품을 선택하세요.");
        return;
      }
      const parentNode = selectedNodes[0];
      const selectedItem =
        parentNode?.data?.cd_matl || parentNode?.data?.cd_item;
      if (!selectedItem) {
        return;
      }
      const noLevel = Number(parentNode?.data?.no_level ?? 0);
      if (noLevel === 0) {
        await showDialog("alter", "최상단 품목은 차수추가할 수 없습니다.");
        return;
      }
      const stBom = String(parentNode?.data?.st_bom_c ?? "")
        .trim()
        .toUpperCase();
      const isOpenBom = stBom === "1" || stBom === "OPEN";
      if (isOpenBom) {
        await showDialog(
          "alter",
          "차수추가 대상이 아닙니다. BOM 상태를 확인해주세요."
        );
        return;
      }

      const component = (
        <SN_PMS_PJT_REG_004_POP_002
          modalRef={ref}
          Menuprops={Menuprops}
          initialParams={{
            cd_pjt: form.cd_pjt,
            cd_unit: form.cd_item,
            cd_item: selectedItem,
            nm_pjt: form.nm_pjt,
            nm_st_pjt: form.nm_st_pjt,
          }}
        />
      );

      const result = await showPopupModal({
        title: "차수추가",
        children: component,
        width: "70%",
        height: "90%",
      });

      if (result && result !== "close") {
        await onSearch();
      }
    }
  };

  const convertToTreeData = (rows: any[]) => {
    if (!rows || rows.length === 0) return [];

    return rows.map((row) => ({
      ...row,

      node: row.node ?? row.cd_matl,
      node_p: row.node_p ?? row.cd_item,
      cd_matl_display: row.cd_matl_display ?? `${row.cd_matl} - ${row.nm_matl}`,
      sort_key: row.sort_key !== undefined ? Number(row.sort_key) : 0,
    }));
  };

  const onRowDataReady = () => {
    // 조회 후 이전 선택/포커스 흔적을 완전히 제거하고 루트 1건만 선택한다.
    const treeApi = gridRefs.current["tree"].current;
    const gridApi = gridRefs.current["grid"].current;
    if (!treeApi || !gridApi) return;

    treeApi.forEachNode?.((node: any) => node?.setSelected?.(false));
    treeApi.deselectAll?.();
    treeApi.clearFocus?.();
    gridApi.clearFocus?.();

    // 렌더 타이밍 이슈로 선택 흔적이 남는 경우가 있어 다음 프레임에서 루트 재선택
    requestAnimationFrame(() => {
      treeApi.forEachNode?.((node: any) => node?.setSelected?.(false));
      treeApi.deselectAll?.();
      treeApi.selectAndFocusRow?.(0);
      // 조회 직후 부모 기준은 루트로 고정
      let rootRow: any = null;
      treeApi.forEachNode?.((node: any) => {
        if (!rootRow && node?.data) rootRow = node.data;
      });
      focusedTreeRowRef.current = rootRow;
    });
  };

  // EBOM 신규 행 초기화 공통 함수
  const initNewRow = (
    row: any,
    parentRow: any,
    form: any,
    baseNoLine: number,
    offset: number
  ) => {
    const parentItem = parentRow.cd_matl || parentRow.cd_item;
    const parentLevel = Number(parentRow.no_level ?? 0);
    const parentDepth = Number(parentRow.depth ?? 0);
    const parentNoLine = Number(parentRow.no_line ?? 0);

    // 공통키
    if (!row.cd_firm) row.cd_firm = userInfo.cd_firm;
    if (!row.cd_pjt) row.cd_pjt = form.cd_pjt;
    if (!row.cd_unit) row.cd_unit = form.cd_item;
    if (row.no_hst === undefined || row.no_hst === null)
      row.no_hst = parentRow.no_hst ?? 0;
    if (!row.no_rev) row.no_rev = parentRow.no_rev ?? "";

    // 트리용 키
    if (!row.node) row.node = uuid();
    if (!row.node_p) row.node_p = parentRow.node;

    // EBOML 구조
    row.cd_item = parentItem;
    if (row.no_line === undefined || row.no_line === null)
      row.no_line = baseNoLine + 1 + offset;
    if (row.no_line_p === undefined || row.no_line_p === null)
      row.no_line_p = parentNoLine;
    if (row.no_seq === undefined || row.no_seq === null)
      row.no_seq = row.no_line;

    // 하위추가는 항상 "현재 선택 부모 레벨 + 1" 규칙으로 강제 세팅
    row.no_level = parentLevel + 1;
    row.indent = `${"…".repeat(Math.max(Number(row.no_level || 0), 0))}${Number(
      row.no_level || 0
    )}`;
    row.depth = parentDepth + 1;

    // 기본값 (현행 컬럼 기준)
    if (row.cd_matl === undefined || row.cd_matl === null) row.cd_matl = "";
    if (!row.nm_matl) row.nm_matl = "";
    if (!row.dc_item_spec) row.dc_item_spec = "";
    if (!row.dc_item_unit) row.dc_item_unit = "";
    if (!row.cd_mgmt01) row.cd_mgmt01 = "";
    if (!row.cd_mgmt02) row.cd_mgmt02 = "";
    if (!row.cd_mgmt03) row.cd_mgmt03 = "";

    if (row.qt_just === undefined || row.qt_just === null || row.qt_just === "")
      row.qt_just = 1;
    if (row.rt_loss === undefined || row.rt_loss === null || row.rt_loss === "")
      row.rt_loss = 0;
    if (row.qt_need === undefined || row.qt_need === null || row.qt_need === "")
      row.qt_need = 1;
    if (!row.dc_rmk) row.dc_rmk = "";
    if (row.fg_eco === undefined || row.fg_eco === null) row.fg_eco = "I";
    if (!row.st_bom) row.st_bom = "1";
  };

  // grid.addRow 직전에 호출되는 공통 초기화 훅 (PUR 메뉴 패턴과 동일)
  const onBeforeAddRowL = () => {
    const gridApi = gridRefs.current["grid"].current;
    const ctx = addParentRef.current;
    if (!gridApi) return false;

    // 셀 멀티팝업 경로(GridV2 공통 addRow 호출)에서는 addParentRef가 없을 수 있다.
    // 이 경우 현재 포커스 행을 기준으로 "같은 부모의 하위행" 기본값을 생성한다.
    if (!ctx?.parentRow) {
      const focusedNode = gridApi.focusedRowNode?.();
      const focusedRow = focusedNode?.data;
      if (!focusedRow) return false;

      const parentItem = focusedRow.cd_item ?? focusedRow.cd_matl;
      if (!parentItem) return false;

      const derivedParentRow: any = {
        cd_firm: focusedRow.cd_firm,
        cd_pjt: focusedRow.cd_pjt,
        cd_item: parentItem,
        cd_matl: parentItem,
        no_hst: focusedRow.no_hst,
        no_rev: focusedRow.no_rev,
        st_bom: focusedRow.st_bom,
        node: focusedRow.node_p ?? focusedRow.cd_item,
        no_line: Number(focusedRow.no_line_p ?? 0),
        no_level: Math.max(Number(focusedRow.no_level ?? 1) - 1, 0),
        depth: Math.max(Number(focusedRow.depth ?? 1) - 1, 0),
      };

      const row: any = {};
      initNewRow(
        row,
        derivedParentRow,
        searchRef.current?.getFormdata?.() ?? {},
        Number(getBaseNoLine(gridApi, parentItem) ?? 0),
        0
      );
      return row;
    }

    const row: any = {};
    const idx = Number(ctx.index ?? 0);
    const offset = Number(ctx.offset ?? idx);
    const src = Array.isArray(ctx.rows) ? ctx.rows[idx] : null;
    initNewRow(
      row,
      ctx.parentRow,
      ctx.form ?? {},
      Number(ctx.baseNoLine ?? 0),
      offset
    );

    // 멀티 팝업에서 넘어온 품목값은 onBeforeAddRow에서 초기값으로 직접 세팅
    if (src) {
      const cdMatl = src?.cd_matl ?? src?.cd_item ?? src?.code;
      const nmMatl = src?.nm_matl ?? src?.nm_item ?? src?.name;
      const dcSpec =
        src?.dc_item_spec ?? src?.dc_item_spec_matl ?? src?.dc_matl_spec;
      const dcUnit =
        src?.dc_item_unit ?? src?.dc_item_unit_matl ?? src?.dc_matl_unit;

      if (cdMatl !== undefined) row.cd_matl = cdMatl;
      if (nmMatl !== undefined) row.nm_matl = nmMatl;
      if (dcSpec !== undefined) row.dc_item_spec = dcSpec;
      if (dcUnit !== undefined) row.dc_item_unit = dcUnit;

      // 관리코드 컬럼명 호환 (cd_mgmt1 / cd_mgmt01)
      if (src?.cd_mgmt1 !== undefined) row.cd_mgmt1 = src.cd_mgmt1;
      if (src?.cd_mgmt2 !== undefined) row.cd_mgmt2 = src.cd_mgmt2;
      if (src?.cd_mgmt3 !== undefined) row.cd_mgmt3 = src.cd_mgmt3;
      if (src?.cd_mgmt01 !== undefined) row.cd_mgmt01 = src.cd_mgmt01;
      if (src?.cd_mgmt02 !== undefined) row.cd_mgmt02 = src.cd_mgmt02;
      if (src?.cd_mgmt03 !== undefined) row.cd_mgmt03 = src.cd_mgmt03;
    }

    ctx.index = idx + 1;
    return row;
  };

  const getBaseNoLine = (gridApi: any, parentItem: string) => {
    let baseNoLine = 0;
    gridApi.forEachNode?.((node: any) => {
      if (node?.data?.cd_item === parentItem) {
        const v = Number(node.data.no_line || 0);
        if (v > baseNoLine) baseNoLine = v;
      }
    });
    return baseNoLine;
  };

  // 선택된 트리 노드 하위로 신규 라인을 추가하고 기본값을 주입
  const addChildRows = async (rows?: any[]) => {
    const treeApi = gridRefs.current["tree"].current;
    const gridApi = gridRefs.current["grid"].current;
    if (!treeApi || !gridApi) return;

    // 부모 기준 통일: 그리드 선택행을 스냅샷으로 고정
    const selectedGridRows = gridApi.getSelectedRows?.() || [];
    const parentRow = selectedGridRows?.[0] || null;
    if (!parentRow) {
      await showDialog("alter", "그리드에서 부모 행을 선택해주세요.");
      return;
    }
    const isOpenBom = (val: any) => {
      const v = String(val ?? "")
        .trim()
        .toUpperCase();
      return v === "1" || v === "OPEN";
    };
    const isInProgress =
      Number(parentRow?.no_hst ?? 0) !== 1 || !isOpenBom(parentRow?.st_bom);
    if (isInProgress) {
      await showDialog("alter", "진행 중인 건은 차수추가로 처리해 주세요.");
      return;
    }
    const parentItem = parentRow.cd_matl || parentRow.cd_item;
    if (!parentItem) {
      await showDialog("alter", "모품목을 확인해주세요.");
      return;
    }

    // 팝업 반환 형식이 배열/객체로 달라도 멀티 행 추가가 되도록 정규화
    const normalizeRows = (input: any): any[] => {
      if (Array.isArray(input)) return input;
      if (Array.isArray(input?.rows)) return input.rows;
      if (Array.isArray(input?.data)) return input.data;
      if (Array.isArray(input?.selectedRows)) return input.selectedRows;
      return [];
    };
    const popupRows = normalizeRows(rows);
    const targetRows = popupRows.length > 0 ? popupRows : [{}];
    const form = searchRef.current?.getFormdata?.() ?? {};
    const baseNoLine = getBaseNoLine(gridApi, parentItem);
    isBatchAddingRef.current = true;
    const addedNodes: string[] = [];
    try {
      // PUR 패턴과 동일하게 "행 단위(addRow 1회)"로 처리해
      // 멀티 선택 시 모든 행이 동일한 onBeforeAddRow 초기화 경로를 타게 한다.
      targetRows.forEach((srcRow: any, idx: number) => {
        addParentRef.current = {
          parentRow,
          form,
          baseNoLine,
          index: 0,
          rows: [srcRow],
          offset: idx,
        };
        const added = gridApi.addRow({});
        if (added?.data?.node) {
          addedNodes.push(String(added.data.node));
        }
      });
    } finally {
      isBatchAddingRef.current = false;
      addParentRef.current = null;
    }
    // 필터가 활성화된 상태라면, 추가된 노드도 필터 값에 포함시켜 가려지지 않게 한다.
    if (nodeFilterRef.current.length > 0 && addedNodes.length > 0) {
      const merged = Array.from(
        new Set([...nodeFilterRef.current, ...addedNodes])
      );
      nodeFilterRef.current = merged;
      gridApi.applyFilters({ node: merged });
    } else if (nodeFilterRef.current.length === 0) {
      gridApi.resetFilters();
    }

    // 주의:
    // addRow 내부에서 이미 "첫 editable 셀(품목코드)"로 포커스를 이동한다.
    // 여기서 다시 selectAndFocusRow를 호출하면 포커스를 덮어써 팝업 오픈 흐름이 깨질 수 있어 호출하지 않는다.

    // 메모:
    // 추가 직후 트리는 즉시 동기화하지 않고 저장/재조회(onSearch) 시점에 반영한다.
  };

  // 선택 노드 기준으로 하위 자식까지 포함한 node 집합 생성
  const collectDescendantNodes = (rows: any[], roots: string[]) => {
    const byParent = new Map<string, string[]>();
    rows.forEach((row) => {
      const node = row?.node ?? row?.cd_matl;
      const nodeP = row?.node_p ?? row?.cd_item;
      if (!node || !nodeP) return;
      if (!byParent.has(nodeP)) {
        byParent.set(nodeP, []);
      }
      byParent.get(nodeP)!.push(node);
    });

    const result = new Set<string>();
    const stack = [...roots];
    while (stack.length > 0) {
      const current = stack.pop() as string;
      if (result.has(current)) continue;
      result.add(current);
      const children = byParent.get(current) || [];
      children.forEach((child) => stack.push(child));
    }
    return result;
  };

  // 그리드 버튼 이벤트(하위추가/삭제) 처리
  const onGridBtnsClick = async (id: string, rows?: any[]) => {
    if (id === "addRow") {
      await addChildRows(rows);
    }
    if (id === "deleteRow") {
      const gridApi = gridRefs.current["grid"].current;
      if (!gridApi) return;

      const selectedRows = gridApi.getSelectedRows?.() || [];
      if (selectedRows.length === 0) {
        await showDialog("alter", "삭제할 행을 선택해 주세요.");
        return;
      }

      const isOpenBom = (val: any) => {
        const v = String(val ?? "")
          .trim()
          .toUpperCase();
        return v === "1" || v === "OPEN";
      };
      const hasInvalid = selectedRows.some(
        (row: any) => Number(row?.no_hst ?? 0) !== 1 || !isOpenBom(row?.st_bom)
      );
      if (hasInvalid) {
        await showDialog("alter", "진행 중인 건은 차수추가로 처리해 주세요.");
        return;
      }

      const allRows = dataSources.grid || [];
      const rootNodes = selectedRows
        .map((row: any) => row?.node ?? row?.cd_matl)
        .filter((v: any) => !!v);
      const targetNodes = collectDescendantNodes(allRows, rootNodes);

      if (targetNodes.size === 0) {
        return;
      }

      const rootNodeSet = new Set(rootNodes.map((v: any) => String(v)));
      const hasChildren = Array.from(targetNodes).some(
        (nodeKey) => !rootNodeSet.has(String(nodeKey))
      );
      if (hasChildren) {
        const ok = await showDialog(
          "confirmCancel",
          "하위자재가 있습니다. 삭제하시겠습니까?"
        );
        if (!ok) {
          return;
        }
      }

      const targetIndexes: number[] = [];
      gridApi.forEachNode?.((node: any) => {
        const nodeKey = node?.data?.node ?? node?.data?.cd_matl;
        if (nodeKey && targetNodes.has(nodeKey)) {
          if (typeof node.rowIndex === "number") {
            targetIndexes.push(node.rowIndex);
          }
        }
      });

      targetIndexes
        .sort((a, b) => b - a)
        .forEach((rowIndex) => gridApi.deleteRowByIndex?.(rowIndex));

      // 메모:
      // 삭제 직후 트리는 즉시 동기화하지 않고 저장/재조회(onSearch) 시점에 반영한다.
    }
  };

  // 부가항목 설정 팝업을 열고 저장 시 컬럼/플래그/조회데이터를 재적용한다.
  const onOpenAddInfoPopup = async (idTarget = "grid2") => {
    // id_target에 해당하는 현재 컬럼 메타를 설정 팝업으로 전달한다.
    const columnInfos = idTarget === "grid1" ? columns.tree || [] : columns.grid || [];
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos,
    });

    // 팝업에서 설정 변경 후 즉시 컬럼/플래그/데이터 재적용
    if (saved) {
      const rtn: any = await getInitFormData();
      setFormData(rtn.formData);
      setFormControlData(rtn.formControlData);
      setColums({
        tree: rtn.grid.grid1,
        grid: rtn.grid.grid2,
        sheet1: rtn.grid.sheet1,
      });
      await loadAddInfoFlags(idTarget);
      await onSearch();
    }
  };

  const onGridExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    switch (event.id) {
      case "extra_item_setting":
        await onOpenAddInfoPopup("grid2");
        break;
      default:
        break;
    }
  };

  const grids = useMemo(
    () => ({
      tree: {
        columns: columns.tree,
        dataSource: dataSources.tree,
        events: {
          onFocusedRowChanged,
          onRowDataReady: onRowDataReady,
        },
      },
      grid: {
        columns: columns.grid,
        dataSource: dataSources.grid,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onBtnAddClick: onGridBtnsClick,
          onBtnDeleteClick: onGridBtnsClick,
          onExtraHeaderMenuClick: onGridExtraHeaderMenuClick,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <style>{`
        .sn-pms-pjt-reg-004-search-panel > div > div:nth-child(2) {
          display: none;
        }
        .sn-pms-pjt-reg-004-search-panel > div > hr {
          display: none;
        }
        .sn-pms-pjt-reg-004-search-panel > div > div:first-child > div {
          overflow-x: hidden !important;
        }
      `}</style>
      <div className="w-full mb-1.5">
        <SnTab
          titles={["실적정보"]}
          width={"100%"}
          selectedTab={"1"}
          onTabChanged={() => {}}
          header={[
            <div className="pr-1">
              <SnButton
                text="부가항목설정"
                onClick={() => onOpenAddInfoPopup("grid2")}
              />
            </div>,
          ]}
          body={[
            <div className="w-full border border-[#babfc7] px-2 py-2 sn-pms-pjt-reg-004-search-panel">
              <SearchPanel
                ref={searchRef}
                data={formData}
                controls={formControlData}
                onValueChanged={onSearchPanelValueChanged}
              />
            </div>,
          ]}
        />
      </div>

      {/* 기존 div에 flex-1을 추가하여 상단 영역이 남은 높이를 채우도록 수정 */}
      <div className="w-full flex flex-1 min-h-0 space-x-1.5 mb-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="25%">
          <SnTree
            ref={gridRefs.current["tree"]}
            columnConfig={grids.tree.columns}
            rowData={grids.tree.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "품목정보",
            }}
            header={
              <div>
                <SnButton
                  text={isTreeExpanded ? "전체닫기" : "전체전개"}
                  onClick={() => onGrid1BtnsClick("btnToggleExpand")}
                />
                <SnButton
                  text="차수추가"
                  onClick={() => onGrid1BtnsClick("btnPhaseAdd")}
                />
              </div>
            }
            events={grids.tree.events}
            treeProps={{
              treeDataParentIdField: "node_p",
              treeIdKey: "node",
              treeOrderKey: "sort_key",
              groupDefaultExpanded: 5,
              autoGroupColumnDef: autoGroupColumnDef,
              getRowId: getRowId,
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="74.5%">
          <SnGrid
            ref={gridRefs.current["grid"]}
            columnConfig={grids.grid.columns}
            rowData={grids.grid.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "BOM정보",
              extraHeaderMenuItems: GRID_EXTRA_HEADER_MENUS,
            }}
            events={grids.grid.events}
            header={
              <div>
                <SnButton
                  text="하위추가"
                  onClick={() => onGridBtnsClick("addRow")}
                />
              </div>
            }
          />
        </SnResizeBar>
      </div>

      <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />

      <div style={{ display: "none" }}>
        <SnGrid
          ref={sheetref1}
          columnConfig={grids.sheet1.columns}
          rowData={grids.sheet1.dataSource} // Datasource
          etcProps={{ isEditable: false }}
        />
      </div>
    </div>
  );
});

export default SN_PMS_PJT_REG_004;
